import type { ComponentType } from "react";
import React from "react";

import type { IButtonProps } from "@similarweb/ui-components";
import { Badge, IconButton, PlainTooltip } from "@similarweb/ui-components";

import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import GAVerifiedContainer from "app/components/React/GAVerifiedIcon/GAVerifiedContainer";
import { FooterComponent } from "app/components/widget/WidgetFooter";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter, percentageFilter, swNumberFilter, timeFilter } from "app/filters/ngFilters";
import { handleCTAClick, TestCases } from "app/common/utils/handleGainAccessRedirect";
import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { EHooksVersions } from "app/services/SwHook";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";

import {
    Header,
    MetricContainer,
    MetricIcon,
    MetricLeftSide,
    MetricName,
    MetricsColumn,
    MetricsContainer,
    MetricValue,
    MetricValueContainer,
    SubTitleContainer,
    TitleContainer,
    TooltipIcon,
    WidgetContainer,
    WidgetContentContainer,
    WidgetFooterContainer,
    WidgetHeaderContainer,
} from "./styles";

const DefaultUpsellButton = (props: IButtonProps) => (
    <IconButton
        iconName="sparkles"
        type="trialOutline"
        data-automation="engagement-overview-kpi-upgrade"
        {...props}
    >
        {translate("common.upgrade").toUpperCase()}
    </IconButton>
);

export type EngagementOverviewKpiTableProps = {
    upsellButton?: ComponentType<IButtonProps>;
    getCtaLink(params: any): { href: string; target?: string; onClick?: () => void };
    data?: any;
    unlockHook?: any;
    params: any;
};

export const EngagementOverviewKpiTable = ({
    data = {} as any,
    params,
    getCtaLink,
    unlockHook = null,
    upsellButton: UpsellButton = DefaultUpsellButton,
}: EngagementOverviewKpiTableProps) => {
    data.Source = data.Source ? data.Source : "Total";
    const i18n = i18nFilter();
    const { selectorHandlers } = useSelectorTrigger("wwo-engagement-overview");
    const [dimensions, setDimensions] = React.useState({
        width: window.innerWidth,
    });
    React.useEffect(() => {
        function handleResize() {
            setDimensions({
                width: window.innerWidth,
            });
        }

        window.addEventListener("resize", handleResize, { capture: true });
        return () => window.removeEventListener("resize", handleResize, { capture: true });
    });
    const metrics = {
        AvgMonthVisits: {
            name: params.duration === "28d" ? i18n("wa.ao.graph.avgvisitsdaily") : i18n("wa.ao.graph.avgvisits"),
            icon: "visits",
            tooltip:
                params.duration === "28d"
                    ? i18n("wa.ao.graph.avgvisits.tooltip")
                    : i18n("wa.ao.graph.avgvisits.tooltip"),
            filter: minVisitsAbbrFilter,
            beta: false,
            new: false,
        },
        UniqueUsers: {
            name: params.duration === "28d" ? i18n("metric.dailyuniquevisitors") : i18n("metric.monthlyuniquevisitors"),
            icon: "monthly-unique-visitors",
            tooltip:
                params.duration === "28d"
                    ? data.isGAVerified
                        ? i18n("metric.dailyuniquevisitors.last28days.ga.error")
                        : i18n("metric.dailyuniquevisitors.tooltip")
                    : i18n("wa.ao.graph.muv.tooltip"),
            filter: minVisitsAbbrFilter,
            beta: false,
            new: params.duration === "28d",
        },
        DedupUniqueUsers: {
            name: i18n("wa.ao.metric.dedup"),
            icon: "users-tab",
            tooltip: i18n("wa.ao.metric.dedup.tooltip"),
            filter: minVisitsAbbrFilter,
            new: false,
        },
        AvgVisitDuration: {
            name: i18n("analysis.audience.geo.table.columns.AvgTime.title"),
            icon: "avg-visit-duration",
            tooltip: i18n("wa.ao.graph.avgduration.tooltip"),
            filter: timeFilter,
            beta: false,
            new: false,
        },
        PagesPerVisit: {
            name: i18n("analysis.single.audience.overview.visits.page"),
            icon: "pages-per-visit",
            tooltip: i18n("wa.ao.graph.pages.tooltip"),
            filter: swNumberFilter,
            beta: false,
            new: false,
        },
        BounceRate: {
            name: i18n("analysis.single.audience.overview.visits.bounce"),
            icon: "bounce-rate-2",
            tooltip: i18n("wa.ao.graph.bounce.tooltip"),
            filter: percentageFilter,
            beta: false,
            new: false,
        },
    };
    const ctaLink = getCtaLink({
        key: data.Domain,
        country: params.country,
        duration: params.duration,
        webSource: data.Source,
        isWWW: "*",
    });
    const getMetrics = () => {
        const tempMetrics = { ...metrics };
        data.isGAVerified ? delete tempMetrics.DedupUniqueUsers : null;
        data.Source !== "Total" ? delete tempMetrics.DedupUniqueUsers : null;
        const metricsArr = Object.entries(tempMetrics);
        return metricsArr.map((metric) => getMetricComponent(metric));
    };
    const getMetricComponent = (metric) => {
        const onClick = () => {
            handleCTAClick(EHooksVersions.V5, "DeduplicatedAudience", TestCases.TRIAL);
        };
        const noLast28 = params.duration === "28d" ? metric[0] === "DedupUniqueUsers" : false;
        let lockedMetric = false;
        metric[0] === "DedupUniqueUsers" ? (lockedMetric = !params.DedupPermission) : null;
        metric[0] === "UniqueUsers" ? (lockedMetric = !params.uniqueVisitorsPermission) : null;
        let value;
        if (data[metric[0]]) {
            switch (metric[1].filter) {
                case minVisitsAbbrFilter:
                    value = metric[1].filter()(data[metric[0]]);
                    break;
                case timeFilter:
                    value = metric[1].filter()(data[metric[0]]);
                    break;
                case swNumberFilter:
                    value = metric[1].filter()(data[metric[0]], 2);
                    break;
                case percentageFilter:
                    value = data[metric[0]] ? metric[1].filter()(data[metric[0]], 2) + "%" : "N/A";
                    break;
            }
        } else {
            value = "N/A";
        }

        return (
            <MetricContainer key={metric[1].name}>
                <MetricLeftSide>
                    <MetricIcon iconName={metric[1].icon} />
                    <MetricName>{metric[1].name}</MetricName>
                    <PlainTooltip tooltipContent={metric[1].tooltip}>
                        <div>
                            <TooltipIcon iconName="info" />
                        </div>
                    </PlainTooltip>
                    {metric[1].beta ? (
                        <span style={{ marginLeft: 5 }}>
                            <Badge type="beta" />
                        </span>
                    ) : null}
                    {metric[1].new ? (
                        <span style={{ marginLeft: 5 }}>
                            <Badge type="new" />
                        </span>
                    ) : null}
                </MetricLeftSide>
                {lockedMetric ? (
                    <UpsellButton
                        onClick={onClick}
                        {...({ isExploreOurPackages: true } as IButtonProps)}
                    />
                ) : noLast28 ? (
                    <div>{i18n("metric.uniquevisitors.nowindow.message")}</div>
                ) : (
                    <MetricValueContainer>
                        <MetricValue>{value}</MetricValue>
                        {data.isGAVerified ? (
                            <GAVerifiedContainer
                                isActive={data.isGAVerified}
                                isPrivate={data.isGAPrivate}
                                tooltipAvailable={true}
                                tooltipIsActive={false}
                                size={"SMALL"}
                                metric={params.metric}
                            />
                        ) : null}
                    </MetricValueContainer>
                )}
            </MetricContainer>
        );
    };
    const { from, to } = DurationService.getDurationData(params.duration).forAPI;
    const getWebSourceName = (source) => {
        switch (source) {
            case "Total":
                return "All Traffic";
            case "Desktop":
                return "Desktop Only";
            case "MobileWeb":
                return "Mobile Web Only";
        }
    };
    const subtitleFilters = [
        {
            filter: "date",
            value: {
                from,
                to,
            },
            onClick: selectorHandlers.onDateClick,
            onMouseEnter: selectorHandlers.onDateHover,
        },
        {
            filter: "country",
            countryCode: params.country,
            value: CountryService.getCountryById(params.country).text,
            onClick: selectorHandlers.onCountryClick,
            onMouseEnter: selectorHandlers.onCountryHover,
        },
        {
            filter: "webSource",
            value: data.Source,
            displayName: getWebSourceName(data.Source),
            onMouseEnter: selectorHandlers.onWebSourceHover,
        },
    ];
    const getMetricsComponent = (width, metrics) => {
        if (width < 1481) {
            const part1 = metrics.slice(0, 3);
            const part2 = metrics.slice(3);
            return (
                <MetricsContainer>
                    <MetricsColumn width={"45%"}>{part1}</MetricsColumn>
                    <MetricsColumn width={"45%"}>
                        {part2}
                        {part2.length < 3 ? <MetricContainer /> : null}
                    </MetricsColumn>
                </MetricsContainer>
            );
        } else {
            const part1 = metrics.slice(0, 2);
            const part2 = metrics.slice(2, 4);
            const part3 = metrics.slice(4);
            return (
                <MetricsContainer>
                    <MetricsColumn>{part1}</MetricsColumn>
                    <MetricsColumn>{part2}</MetricsColumn>
                    <MetricsColumn>
                        {part3}
                        {part3.length < 2 ? <MetricContainer /> : null}
                    </MetricsColumn>
                </MetricsContainer>
            );
        }
    };

    return (
        <WidgetContainer>
            <WidgetHeaderContainer>
                <TitleContainer>
                    <Header>{i18n("wa.ao.engagement.overview")}</Header>
                    <PlainTooltip tooltipContent={i18n("wa.ao.engagement.overview.tooltip")}>
                        <div>
                            <TooltipIcon iconName="info" />
                        </div>
                    </PlainTooltip>
                </TitleContainer>
                <SubTitleContainer>
                    <StyledBoxSubtitle>
                        <BoxSubtitle filters={subtitleFilters} />
                    </StyledBoxSubtitle>
                </SubTitleContainer>
            </WidgetHeaderContainer>
            <WidgetContentContainer>{getMetricsComponent(dimensions.width, getMetrics())}</WidgetContentContainer>
            <WidgetFooterContainer>
                <FooterComponent
                    href={ctaLink.href}
                    text="wwo.overview.engagement.over.time.button"
                    unlockHook={unlockHook}
                    target={ctaLink.target}
                    onClick={ctaLink.onClick}
                />
            </WidgetFooterContainer>
        </WidgetContainer>
    );
};
